import mixpanel from "mixpanel-browser";
import * as amplitude from "@amplitude/analytics-browser";
import { dumpLogs, loggedSession } from "../hooks/dumpLogs";
import { Capacitor } from "@capacitor/core";
import { getStudentFromStorage } from "@helpers/migrateLogin";
import { PAGE_CONTEXT_NAME, REGION } from "@utils/constants";
import Analytics, { AnalyticsInstance } from "analytics";
import mixpanelPlugin from "@analytics/mixpanel";
import amplitudePlugin from "@analytics/amplitude";
import { getDistinctId } from "@helpers/index";
import { trimArrayToMaxLength } from "@utils/helpers";
import { isNativeApp } from "@utils/helper/isSome";

let mixpanelInstance: any = null; // Store Mixpanel instance at the module level
let amplitudeInstance: any = null;
let clevertapInstance: any = null;

// Dynamically import CleverTap only on client side
let clevertap: any = null;
let isBrowser = typeof window !== "undefined";

const clevertapPlugin = () => {
    const accountId = process.env.NEXT_PUBLIC_CLEVERTAP_ACCOUNT_ID!;
    const region = process.env.NEXT_PUBLIC_CLEVERTAP_REGION! || "eu1";

    if (!accountId) {
        console.error(
            "CleverTap account ID is missing. Please provide a valid account ID.",
        );
        return;
    }

    const clevertapInit = async () => {
        await new Promise(async (resolve, reject) => {
            try {
                // Dynamically import CleverTap on client side
                if (!clevertap) {
                    clevertap = await require("clevertap-web-sdk");
                }

                // Ensure clevertap is initialized properly
                if (!clevertap.init) {
                    throw new Error("CleverTap SDK is not loaded correctly.");
                }

                clevertap.init(accountId);

                // Ensure methods are available before using
                if (clevertap.setOptOut) {
                    clevertap.setOptOut(false);
                } else {
                    console.warn(
                        "CleverTap setOptOut method is not available.",
                    );
                }

                if (clevertap.privacy?.setOptOut) {
                    clevertap.privacy.setOptOut(false);
                }

                clevertap.setLogLevel(1);

                console.log("CleverTap initialized successfully");
                resolve(true);
            } catch (err) {
                console.error("Failed to initialize CleverTap:", err);
                reject(err);
            }
        });
    };

    // Return analytics plugin object
    return {
        name: "clevertap",
        initialize: async () => {
            return;
        },
        // Identify user
        identify: async ({ payload }) => {
            await clevertapInit();
            if (!isBrowser || !clevertap) return; // Skip if not browser or not initialized

            const { userId, traits } = payload;
            if (userId) {
                try {
                    // Format traits for CleverTap (camelCase to PascalCase)
                    const formattedTraits = flattenObject(traits || {});

                    // Log before sending data
                    console.log("Identifying user:", userId, formattedTraits);
                    if (formattedTraits?.phone?.length == 10) {
                        formattedTraits["Phone"] =
                            `+91${formattedTraits.phone}`;
                    } else if (formattedTraits?.phone) {
                        formattedTraits["Phone"] = formattedTraits.phone;
                    }
                    if (formattedTraits?.email)
                        formattedTraits["Email"] = formattedTraits.email;

                    if (clevertap.onUserLogin) {
                        clevertap.onUserLogin.push({
                            Site: {
                                Identity: userId,
                                ...formattedTraits,
                                "MSG-email": true, // Disable email notifications
                                "MSG-push": true, // Enable push notifications
                                "MSG-sms": true, // Enable sms notifications
                                "MSG-whatsapp": true,
                            },
                        });
                        console.log("User identified successfully");
                    } else {
                        console.warn(
                            "CleverTap onUserLogin method is not available.",
                        );
                    }

                    if (clevertap?.addMultiValueForKey) {
                        const params = new URLSearchParams(
                            window.location.search,
                        );
                        const filteredParams = getFilteredQueryParams(params);
                        filteredParams.forEach(({ key, value }) => {
                            clevertap.addMultiValueForKey(
                                "query_param_keys_v2",
                                key,
                            );
                            clevertap.addMultiValueForKey(
                                "query_param_values_v2",
                                value,
                            );
                        });
                    }
                } catch (err) {
                    console.error("Error identifying user:", err);
                }
            }
        },
        // Track events
        track: async ({ payload }) => {
            if (!isBrowser || !clevertap) return; // Skip if not browser or not initialized

            const { event, properties } = payload;
            if (event) {
                try {
                    const flattenedProps = flattenObject(properties || {});
                    // Log before sending event data
                    // console.log(`Tracking event: ${event}`, flattenedProps);

                    if (clevertap.event && clevertap.event.push) {
                        clevertap.event.push(event, flattenedProps);
                        // console.log(`Event ${event} tracked successfully`);
                    } else {
                        console.warn(
                            "CleverTap event.push method is not available.",
                        );
                    }
                } catch (err) {
                    console.error("Error tracking event:", err);
                }
            }
        },
        // Page views
        page: async ({ payload }) => {
            if (!isBrowser || !clevertap) return; // Skip if not browser or not initialized

            const { properties } = payload;
            try {
                const flattenedProps = flattenObject(properties || {});
                // Log before sending page view event data
                console.log("Tracking page view:", flattenedProps);

                if (clevertap.event && clevertap.event.push) {
                    clevertap.event.push("Page Viewed", flattenedProps);
                    console.log("Page view tracked successfully");
                } else {
                    console.warn(
                        "CleverTap event.push method is not available.",
                    );
                }
            } catch (err) {
                console.error("Error tracking page view:", err);
            }
        },
        // Reset/logout user
        reset: async () => {
            if (!isBrowser || !clevertap) return; // Skip if not browser or not initialized

            try {
                // CleverTap doesn't have a direct logout method
                // We can push a logout event instead
                if (clevertap.event && clevertap.event.push) {
                    clevertap.event.push("User Logged Out");
                    console.log("User logged out successfully");
                } else {
                    console.warn(
                        "CleverTap event.push method is not available.",
                    );
                }
            } catch (err) {
                console.error("Error logging out user:", err);
            }
        },
    };
};

// notification-service events ingest endpoint (gen2 Cloud Function). Analytics
// events are mirrored here; the ingest filters against its own allow-list.
const INGEST_EVENTS_URL =
    process.env.NEXT_PUBLIC_INGEST_EVENTS_URL ||
    "https://asia-south1-mathai-449208.cloudfunctions.net/ingestEvents";

// Extract the numeric profile id from a distinct_id of the form
// `{mobile}_{name}_{id}` (e.g. "9942114336_swetha_114254" -> "114254"). Returns
// undefined when the trailing segment isn't purely numeric (e.g. a UUID).
const numericIdFromDistinctId = (distinctId?: string): string | undefined => {
    if (!distinctId) return undefined;
    const last = distinctId.split("_").pop();
    return last && /^\d+$/.test(last) ? last : undefined;
};

// Resolve the personalized_learning_user.id (integer) the ingest keys on.
const resolveIngestUserId = (
    id: any,
    distinctId?: string,
): string | undefined => {
    if (
        id !== undefined &&
        id !== null &&
        id !== "" &&
        /^\d+$/.test(String(id))
    )
        return String(id);
    return numericIdFromDistinctId(distinctId);
};

// Flatten nested objects
const flattenObject = (obj: any, prefix = ""): any => {
    let flattened: any = {};

    for (const key in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
        const newKey = prefix ? `${prefix}_${key}` : key;

        if (typeof obj[key] === "object" && obj[key] !== null) {
            // Instead of flattening, store the entire object as a JSON string
            flattened[newKey] = JSON.stringify(obj[key]);
        } else {
            flattened[newKey] = obj[key];
        }
    }

    return flattened;
};

// Helper function to format phone number
const formatPhoneNumber = (phone: string) => {
    // Check if phone number starts with country code, else prepend +91 (assuming India)
    if (!phone.startsWith("+")) {
        return `+91${phone}`;
    }
    return phone;
};

function init() {
    if (!mixpanelInstance) {
        mixpanelInstance.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN!, {
            persistence: "localStorage",
            ignore_dnt: true,
            batch_requests: true,
            api_host: process.env.NEXT_PUBLIC_MIXPANEL_PROXY_URL,
        });
    }

    if (!amplitudeInstance) {
        amplitudeInstance = amplitude;
        amplitudeInstance.init(process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY!, {
            saveEvents: true, // Ensures events are stored if offline
            includeUtm: true, // Tracks UTM parameters
            includeReferrer: true, // Tracks referrer info
            disableCookies: false, // Enables cookies for tracking
            defaultTracking: false,
        });
    }
}

function appendToUserTraits(key: string, value: any) {
    if (!mixpanelInstance && !amplitudeInstance) {
        console.warn(
            "Analytics instances are not initialized. Initializing now...",
        );
        init();
    }

    // Mixpanel append
    if (mixpanelInstance?.people) {
        mixpanelInstance.people.append(key, value);
    }

    // Amplitude append
    if (amplitudeInstance) {
        const identifyObj = new amplitude.Identify().append(key, value);
        amplitudeInstance.identify(identifyObj);
    }

    // Clevertap append
    if (clevertapInstance) {
        clevertapInstance.addMultiValueForKey(key, value);
    }
}

function getFilteredQueryParams(params: URLSearchParams) {
    let paramsToInclude: string[] = ["sid", "c"];

    if (process.env.NEXT_PUBLIC_MIXPANEL_ALLOWED_PARAMS) {
        try {
            paramsToInclude = JSON.parse(
                process.env.NEXT_PUBLIC_MIXPANEL_ALLOWED_PARAMS,
            );
        } catch (err) {
            console.warn(
                "Failed to parse NEXT_PUBLIC_MIXPANEL_ALLOWED_PARAMS, using default:",
                err,
            );
        }
    }

    const filteredParams: Record<string, string>[] = [];
    params.forEach((value, key) => {
        if (key === "returnTo") {
            const newParams = new URLSearchParams(value.split("?")[1]);
            newParams.forEach((value, key) => {
                if (paramsToInclude.includes(key)) {
                    filteredParams.push({
                        key,
                        value,
                    });
                }
            });
        }
        if (paramsToInclude.includes(key)) {
            filteredParams.push({
                key,
                value,
            });
        }
    });
    return filteredParams;
}

class MixpanelEventClass {
    student = {};
    mathai_platform = "";
    activity_type = "CHALLENGE";
    query_param_keys: any = [];
    query_param_values: any = [];
    referral_params: any = {};
    deeplink_url: string = "";

    user_is_currently_on = PAGE_CONTEXT_NAME.direct;
    referrer_screen = [PAGE_CONTEXT_NAME.direct];
    referrer_screen_info = "";
    analytics: AnalyticsInstance | null = null;
    distinct_id = "";

    init() {
        if (this.analytics) {
            return;
        }

        // Initialize analytics with mixpanel plugin
        this.analytics = Analytics({
            app: "awesome-app",
            plugins: [
                mixpanelPlugin({
                    token: process.env.NEXT_PUBLIC_MIXPANEL_TOKEN!,
                    options: {
                        persistence: "localStorage",
                        ignore_dnt: true,
                        batch_requests: true,
                        api_host: process.env.NEXT_PUBLIC_MIXPANEL_PROXY_URL,
                    },
                }),
                amplitudePlugin({
                    apiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY!,
                }),
                clevertapPlugin(),
            ],
        });

        this.mathai_platform = Capacitor.getPlatform();

        // Initialize Mixpanel and store it in the module variable
        mixpanelInstance = mixpanel;
        mixpanelInstance.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN!, {
            persistence: "localStorage",
            ignore_dnt: true,
            batch_requests: true,
            api_host: process.env.NEXT_PUBLIC_MIXPANEL_PROXY_URL,
        });

        // Initialize Amplitude and store it in the module variable
        amplitudeInstance = amplitude;
        amplitudeInstance.init(process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY!, {
            saveEvents: true, // Ensures events are stored if offline
            includeUtm: true, // Tracks UTM parameters
            includeReferrer: true, // Tracks referrer info
            disableCookies: false, // Enables cookies for tracking
            defaultTracking: false,
        });

        // Store reference to CleverTap
        clevertapInstance = clevertap;
    }

    identify(userId: string) {
        if (this.distinct_id === userId) {
            // user already identified, prevent duplicate entries
            return;
        }
        const student = JSON.parse(getStudentFromStorage() || "{}");
        if (student) {
            const other = JSON.parse(
                typeof student?.other === "string" ? student?.other : `{}`,
            );
            this.student = {
                id: student.id,
                name: student.name,
                mobile: student.mobile,
                grade: student.class_name,
                type: student.type,
                parent_name: other?.parent_name,
                email: student.email,
                school_name: other?.school_name,
            };
        }
        this.analytics?.identify(userId);
        let other = student?.other || {};
        try {
            other = JSON.parse(student?.other);
        } catch {}
        this.distinct_id = getDistinctId(student) || userId;

        // For native apps, use deeplink URL if available, otherwise use window.location
        let urlToUse = window.location.href;
        if (isNativeApp() && this.deeplink_url) {
            urlToUse = this.deeplink_url;
        }

        // Extract query params from the URL
        const urlObj = new URL(
            urlToUse,
            urlToUse.startsWith("http") ? undefined : "https://localhost",
        );
        const params = new URLSearchParams(urlObj.search);

        const filteredParams = getFilteredQueryParams(params);
        filteredParams.forEach(({ key, value }) => {
            MixpanelEvent.addKeyValueArray(key, value);
        });

        this.analytics?.identify(getDistinctId(student), {
            name: student.name,
            email: student.email,
            phone: student.mobile,
            grade: student.class_name,
            type: student.type,
            gender: student.gender,
            access_type: student.access_type,
            parent_name: other?.parent_name,
            maths_feeling: other?.maths_feeling,
            pervious_marks: other?.pervious_marks,
            // language: other?.language,
            competition_prep: other?.competition_prep,
            goals: other?.goals,
            goals_commitment: other?.goals_commitment,
            profile_picture: other?.profile_picture,
            school_name: other?.school_name,
            profile_id: student.id,
            profile_id_with_region: `${student.id}_${REGION}`,
            region: REGION,
            // query_param_keys: [],
            // query_param_values: []
        });
    }
    defaultEvents = { utm_source: "" };
    referrer_screen_params = [
        "worksheet_id",
        "utm_source",
        "activity_id",
        "typ",
        "ref",
        "ch",
    ];
    getReferrerQueryValues(query) {
        const referrerQueryValues: any = {};
        this.referrer_screen_params.forEach((param) => {
            if (query[param]) {
                referrerQueryValues[param] = query[param];
            }
        });
        return Object.keys(referrerQueryValues)
            .map((key) => `${key}:${referrerQueryValues[key]}`)
            .join("__");
    }
    updatePageLocation = (currentScreen, router) => {
        const { asPath, isReady, pathname, query } = router;
        this.referrer_screen_info = this.getReferrerQueryValues(query);
        this.user_is_currently_on = currentScreen;
        // console.trace('this', currentScreen)
        this.referrer_screen.push(currentScreen);
    };
    addEditionRoutes(routes: Array<any>) {
        if (routes.length > 0) {
            this.referrer_screen.push(...routes);
        }
    }
    track(event: string, data?: any) {
        if (event === "screen_change") {
            console.log(
                "screen_change",
                this.user_is_currently_on,
                this.referrer_screen,
            );
        }
        const logged_session_id = loggedSession.logged_session_id;
        const student = JSON.parse(getStudentFromStorage() || "{}");
        const eventData = {
            ...this.defaultEvents,
            user_is_currently_on: this.user_is_currently_on,
            user_came_from: trimArrayToMaxLength(this.referrer_screen),
            referrer_screen_info: this.referrer_screen_info,
            ...data,
            ...this.student,
            mathai_platform: this.mathai_platform,
            ...(logged_session_id ? { logged_session_id } : {}),
            activity_type: this.activity_type,
            current_href: window?.location?.href ?? "",
            profile_id_with_region: `${student.id}_${REGION}`,
            region: REGION,
        };
        this.analytics?.track(
            event,
            eventData,
            {
                plugins: {
                    mixpanel: false, // Disable Mixpanel plugin for track event
                },
            },
            (...e) => {
                if (e[0] !== 1)
                    dumpLogs({
                        action: "mixpanel_track_fail",
                        ...this.student,
                        event,
                        data,
                    });
            },
        );
        this.sendIngestEvent(event, eventData); // 🆕 Mirror to notification-service ingest
    }

    // Fire-and-forget mirror of a track event to the notification-service ingest
    // API. The ingest filters against its own allow-list, so sending every event
    // is intentional — unmatched events are dropped server-side. Never throws.
    sendIngestEvent(event: string, data: any) {
        try {
            if (!event) return;
            const userId = resolveIngestUserId(
                (this.student as any)?.id,
                this.distinct_id,
            );
            // No numeric profile id available — can't key the user, skip.
            if (!userId) return;
            fetch(INGEST_EVENTS_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    event,
                    user_id: userId,
                    data: data ?? {},
                }),
                keepalive: true,
            }).catch(() => {});
        } catch {}
    }
    appendToUserTraits(key, value) {
        if (!this.analytics) {
            this.init();
        }

        appendToUserTraits(key, value);
    }

    addKeyValueArray(key, value) {
        // Ensure both key and value are appended correctly
        this.appendToUserTraits("query_param_keys_v2", key);
        this.appendToUserTraits("query_param_values_v2", value);
    }

    getStudent() {
        return {
            distinct_id: this.distinct_id,
            ...this.student,
        };
    }

    setDeeplinkUrl(url: string) {
        this.deeplink_url = url;
        console.log("Deeplink URL stored:", url);

        // If user is already identified, process the deeplink params now
        if (this.distinct_id && isNativeApp()) {
            // Extract query params from the deeplink URL
            const urlObj = new URL(
                url,
                url.startsWith("http") ? undefined : "https://localhost",
            );
            const params = new URLSearchParams(urlObj.search);

            const filteredParams = getFilteredQueryParams(params);
            filteredParams.forEach(({ key, value }) => {
                this.addKeyValueArray(key, value);
            });
        }
    }
}
export const MixpanelEvent = new MixpanelEventClass();
